/**
 * PDP option-axis rules: which variant attributes become selectable steps, what order the
 * steps appear in, how values sort inside a step, and whether a step renders as buttons or a
 * dropdown.
 *
 * These are merchandising rules, not data rules — the same attribute set has to read the same
 * way on every product, so they live in one place rather than being re-derived per component.
 */

/**
 * Attribute keys that are never selectable steps: physical dimensions, pricing inputs, and
 * option lists that get their own dedicated UI (finishes/sheens). They surface as spec rows.
 */
export const NON_AXIS_KEYS = [
  'weight',
  'width',
  'height',
  'length',
  'variant-name',
  'finish-price',
  'finishes',
  'sheens',
  'lead-time',
];

/**
 * Canonical step order: identity choices (what it's made of) before configuration choices
 * (how big / which end treatment). Any key not listed sorts after every known key.
 */
const AXIS_ORDER = [
  'material',
  'wood-type',
  'top-style',
  'mounting-style',
  'infill',
  'color',
  'system-length',
  'overall-length',
  'handrail-height',
  'handrail-plow',
  'tread-width',
  'tread-returns',
];

const UNKNOWN_AXIS_RANK = 99;

/** Customer-facing step labels. Keys not listed fall back to Title Case of the key. */
const AXIS_LABELS: Record<string, string> = {
  material: 'Material',
  'wood-type': 'Wood Type',
  'top-style': 'Top Style',
  'mounting-style': 'Mounting Style',
  infill: 'Infill',
  color: 'Color',
  'system-length': 'System Length',
  'overall-length': 'Length',
  'handrail-height': 'Handrail Height',
  'handrail-plow': 'Handrail Plow',
  'tread-width': 'Tread Width',
  'tread-returns': 'Tread Returns',
};

/**
 * Axes that always render as buttons regardless of how many values they have. These are
 * categorical/identity picks — shoppers browse them visually, so collapsing them into a
 * dropdown hides the choice. Only dimension-like lists (lengths, heights) may collapse.
 */
const ALWAYS_BUTTONS = new Set(['wood-type', 'material', 'color', 'tread-returns', 'tread-width']);

/** Above this many values a non-categorical axis collapses to a dropdown. */
const DROPDOWN_VALUE_THRESHOLD = 10;

/**
 * Merchandising order that beats the default comparator. Values not listed fall to the end and
 * tie-break with the comparator. Add an axis here whenever showroom order ≠ alphabetical.
 */
const VALUE_ORDER_OVERRIDES: Record<string, string[]> = {
  'tread-returns': ['No Return', 'Double Return', 'Left Return', 'Right Return'],
  // Bare wood leads the finish list — it is the default and the cheapest option, so shoppers
  // should meet it before the stains.
  finish: ['Unfinished', 'Natural'],
};

/** Some attribute values arrive as `"Label:#hex"`; only the label is ever displayed or matched. */
export const cleanOptionValue = (value: string) => value.split(':')[0].trim();

export const getAxisLabel = (key: string) =>
  AXIS_LABELS[key] ??
  key
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const startsWithDigit = (value: string) => /^\s*\d/.test(value);

/**
 * "Letters first, numbers last", then numeric-aware alphabetical.
 *
 * Digit-initial values sort after every letter-initial value so named options (e.g. "Aluminum")
 * precede numbered ones (e.g. "304 Stainless Steel"). Numeric awareness keeps lengths reading
 * 1…12 instead of the lexical 1, 10, 11, 2.
 */
export const compareOptionValues = (a: string, b: string) => {
  const aIsNumeric = startsWithDigit(a);
  const bIsNumeric = startsWithDigit(b);
  if (aIsNumeric !== bIsNumeric) return aIsNumeric ? 1 : -1;
  return a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });
};

/**
 * Sorts an axis's values, comparing on the cleaned label so `"Unfinished:#FFFFFF"` matches an
 * override entry of `"Unfinished"` and hex suffixes never affect ordering.
 */
export const sortAxisValues = (axisKey: string, values: string[]) => {
  const override = VALUE_ORDER_OVERRIDES[axisKey];

  const rankOf = (value: string) => {
    if (!override) return -1;
    const label = cleanOptionValue(value).toLowerCase();
    return override.findIndex((entry) => cleanOptionValue(entry).toLowerCase() === label);
  };

  return [...values].sort((a, b) => {
    if (override) {
      // Unlisted values sort after every listed one, then tie-break normally.
      const aRank = rankOf(a);
      const bRank = rankOf(b);
      if (aRank !== -1 && bRank !== -1) return aRank - bRank;
      if (aRank !== -1) return -1;
      if (bRank !== -1) return 1;
    }
    return compareOptionValues(cleanOptionValue(a), cleanOptionValue(b));
  });
};

const axisRank = (key: string) => {
  const rank = AXIS_ORDER.indexOf(key);
  return rank === -1 ? UNKNOWN_AXIS_RANK : rank;
};

export const compareAxes = (aKey: string, bKey: string) => {
  const rankDifference = axisRank(aKey) - axisRank(bKey);
  // Two unknown keys both rank 99 — fall back to the key so ordering stays stable.
  return rankDifference !== 0 ? rankDifference : aKey.localeCompare(bKey);
};

/**
 * Render order for the lead axes: species/material come first because they gate everything else
 * (they change the finish palette and the image). Finish and Sheen are rendered between these
 * and the remaining axes by the configurator — they are not variant axes, so they aren't here.
 */
const LEAD_AXIS_KEYS = ['wood-type', 'material'];

export const orderAxesForRender = <T extends { key: string }>(axes: T[]) => {
  const lead = LEAD_AXIS_KEYS.map((key) => axes.find((axis) => axis.key === key)).filter(
    (axis): axis is T => Boolean(axis),
  );
  const rest = axes
    .filter((axis) => !LEAD_AXIS_KEYS.includes(axis.key))
    .sort((a, b) => compareAxes(a.key, b.key));

  return [...lead, ...rest];
};

/**
 * A single-valued attribute is not a choice — it's a fact about the product, so it belongs in the
 * spec list rather than as a step with one button.
 */
export const isSelectableAxis = (key: string, distinctValueCount: number) =>
  !NON_AXIS_KEYS.includes(key) && distinctValueCount > 1;

export const shouldUseDropdown = (axisKey: string, valueCount: number) =>
  valueCount > DROPDOWN_VALUE_THRESHOLD && !ALWAYS_BUTTONS.has(axisKey);
